// Copyright 2025, University of Maryland, All Rights Reserved
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Trash2, Copy, AppWindow, ChevronDown, ChevronRight, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/forms';
import { useOrganizations, ORGANIZATIONS_QUERY_KEY } from '@/subsystems/workspaces/hooks/use-organizations';
import { useOrganizationApi } from '@/subsystems/workspaces/services/organization-api';
import { useWorkspaceApi, fetchOrgWorkspaces } from '@/subsystems/workspaces/services/workspace-api';
import { EditOrganizationDialog } from '@/subsystems/workspaces/components/organizations/EditOrganizationDialog';
import { WorkspaceWizard } from '@/subsystems/workspaces/components/templates';
import { createWorkspaceTransferService } from '@/subsystems/workspace-transfer';
import { NodeApiClient } from '@/app-shell/services';
import { apiClient } from '../api/api-client';
import type { OrganizationResponse, WorkspaceInfo } from '@/subsystems/workspaces/types/workspace';

const transferService = createWorkspaceTransferService(
  new NodeApiClient(apiClient.getAxiosInstance()),
);

const APP_ID = 'docreview';
const UNGROUPED_KEY = '_ungrouped_';
const UNGROUPED_LABEL = 'Other';

export default function OrgManagementPage() {
  const organizationApi = useOrganizationApi();
  const queryClient = useQueryClient();
  const { data: organizations = [], isLoading } = useOrganizations();

  const [createOrgDialogOpen, setCreateOrgDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<OrganizationResponse | null>(null);
  const [createWsForOrgId, setCreateWsForOrgId] = useState<string | null>(null);

  const createOrgMutation = useMutation({
    mutationFn: (data: { org_id?: string; name: string; description?: string }) =>
      organizationApi.createOrganization(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      toast.success('Organization created');
      setCreateOrgDialogOpen(false);
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to create organization',
      });
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: (orgId: string) => organizationApi.deleteOrganization(orgId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY });
      toast.success('Organization deleted');
      setOrgToDelete(null);
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to delete organization',
      });
    },
  });

  return (
    <div className="min-h-full bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-[1280px] px-6 pt-9 pb-16 sm:px-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[21px] font-[650] tracking-[-0.015em]" data-testid="org-management-page-heading">
            Org & Workspace Manager
          </h1>
          <p className="mt-[5px] text-[13px] text-slate-500">
            Organizations, their workspaces, and application groupings
          </p>
        </div>
        <button
          type="button"
          data-testid="org-create-button"
          onClick={() => setCreateOrgDialogOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#2563eb] px-4 py-2 text-[12.5px] font-semibold text-white transition-all duration-[120ms] hover:brightness-[1.08]"
        >
          <Plus className="h-3.5 w-3.5" />
          Create Organization
        </button>
      </div>

      {isLoading ? (
        <div className="mt-7 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center text-[13px] text-slate-500 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
          Loading organizations…
        </div>
      ) : organizations.length === 0 ? (
        <div className="mt-7 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 text-center shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
          <div className="text-[13.5px] font-[550] text-slate-700">No organizations yet</div>
          <div className="mt-1 text-[12.5px] text-slate-400">Create an organization to get started.</div>
        </div>
      ) : (
        <div className="mt-7 space-y-4">
          {organizations.map((org) => (
            <OrgCard
              key={org.orgId}
              org={org}
              onDeleteOrg={() => setOrgToDelete(org)}
              onCreateWorkspace={() => setCreateWsForOrgId(org.orgId)}
            />
          ))}
        </div>
      )}

      <EditOrganizationDialog
        organization={null}
        open={createOrgDialogOpen}
        onOpenChange={setCreateOrgDialogOpen}
        onSave={(_, data) =>
          createOrgMutation.mutate({
            org_id: data.orgId,
            name: data.name,
            description: data.description,
          })
        }
        isPending={createOrgMutation.isPending}
      />

      <ConfirmDialog
        open={!!orgToDelete}
        onOpenChange={(open) => !open && setOrgToDelete(null)}
        title="Delete organization?"
        description={
          orgToDelete
            ? `This will permanently delete "${orgToDelete.name}" (${orgToDelete.orgId}).`
            : ''
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteOrgMutation.isPending}
        onConfirm={() => {
          if (orgToDelete) deleteOrgMutation.mutate(orgToDelete.orgId);
        }}
        testId="org-delete-confirm"
      />

      {createWsForOrgId && (
        <WorkspaceWizard
          app_id={APP_ID}
          orgId={createWsForOrgId}
          open={!!createWsForOrgId}
          onOpenChange={(open) => !open && setCreateWsForOrgId(null)}
          onWorkspaceCreated={(orgId) => {
            queryClient.invalidateQueries({ queryKey: ['org-workspaces', orgId] });
            setCreateWsForOrgId(null);
          }}
        />
      )}
      </div>
    </div>
  );
}

interface OrgCardProps {
  org: OrganizationResponse;
  onDeleteOrg: () => void;
  onCreateWorkspace: () => void;
}

function OrgCard({ org, onDeleteOrg, onCreateWorkspace }: OrgCardProps) {
  const queryClient = useQueryClient();
  const workspaceApi = useWorkspaceApi();

  const [expanded, setExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const { data: workspaces = [], isLoading } = useQuery<WorkspaceInfo[], Error>({
    queryKey: ['org-workspaces', org.orgId],
    queryFn: () => fetchOrgWorkspaces(org.orgId),
    enabled: expanded,
  });

  const [wsToDelete, setWsToDelete] = useState<WorkspaceInfo | null>(null);
  const [wsToClone, setWsToClone] = useState<WorkspaceInfo | null>(null);
  const [editingWsId, setEditingWsId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const renameWsMutation = useMutation({
    mutationFn: ({ wsId, name }: { wsId: string; name: string }) =>
      workspaceApi.adminUpdateWorkspace(org.orgId, wsId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-workspaces', org.orgId] });
      toast.success('Workspace renamed');
      setEditingWsId(null);
      setEditName('');
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to rename workspace',
      });
    },
  });

  const startEditing = (ws: WorkspaceInfo) => {
    setEditingWsId(ws.wsId);
    setEditName(ws.name || '');
  };

  const cancelEditing = () => {
    setEditingWsId(null);
    setEditName('');
  };

  const commitEditing = (ws: WorkspaceInfo) => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === ws.name) {
      cancelEditing();
      return;
    }
    renameWsMutation.mutate({ wsId: ws.wsId, name: trimmed });
  };

  const deleteWsMutation = useMutation({
    mutationFn: (wsId: string) => workspaceApi.adminDeleteWorkspace(org.orgId, wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-workspaces', org.orgId] });
      toast.success('Workspace deleted');
      setWsToDelete(null);
    },
    onError: (error) => {
      toast.error('Error', {
        description: error instanceof Error ? error.message : 'Failed to delete workspace',
      });
    },
  });

  const grouped = useMemo(() => {
    const groups = new Map<string, { label: string; items: WorkspaceInfo[] }>();
    for (const ws of workspaces) {
      const key = ws.application || UNGROUPED_KEY;
      const label = ws.applicationDisplayName || (ws.application ?? UNGROUPED_LABEL);
      if (!groups.has(key)) groups.set(key, { label, items: [] });
      groups.get(key)!.items.push(ws);
    }
    return Array.from(groups.entries())
      .sort(([, a], [, b]) => a.label.localeCompare(b.label))
      .map(([key, value]) => ({ key, ...value }));
  }, [workspaces]);

  return (
    <section
      className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
      data-testid={`org-card-${org.orgId}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-4">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2.5 text-left"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid={`org-toggle-${org.orgId}`}
        >
          {expanded ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150" />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150" />
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Building2 className="h-4 w-4 flex-none text-slate-400" />
              <span className="truncate text-sm font-semibold tracking-[-0.01em]">{org.name}</span>
              <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600">
                {org.orgId}
              </span>
            </div>
            <p className="mt-0.5 truncate text-[12.5px] text-slate-500">
              {org.description || 'No description'}
            </p>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-1.5">
          <button
            type="button"
            data-testid={`org-create-workspace-${org.orgId}`}
            onClick={onCreateWorkspace}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-[550] text-slate-700 shadow-[0_1px_2px_rgba(2,8,23,0.04)] transition-colors duration-100 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Create Workspace
          </button>
          <button
            type="button"
            data-testid={`org-delete-button-${org.orgId}`}
            onClick={onDeleteOrg}
            aria-label={`Delete ${org.name}`}
            className="inline-flex items-center rounded-lg p-2 text-slate-400 transition-colors duration-100 hover:bg-[#fef2f2] hover:text-[#dc2626]"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[#eef2f6] px-5 py-3">
          {isLoading ? (
            <p className="py-2 text-[13px] text-slate-500">Loading workspaces…</p>
          ) : workspaces.length === 0 ? (
            <p className="py-2 text-[13px] text-slate-500">No workspaces yet.</p>
          ) : (
            <div className="space-y-2">
              {grouped.map((group) => {
                const groupOpen = !!expandedGroups[group.key];
                return (
                  <div key={group.key}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-lg px-1 py-1.5 text-left transition-colors duration-100 hover:bg-slate-50"
                      onClick={() =>
                        setExpandedGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))
                      }
                      aria-expanded={groupOpen}
                      data-testid={`app-group-toggle-${org.orgId}-${group.key}`}
                    >
                      {groupOpen ? (
                        <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                      )}
                      <AppWindow className="h-3.5 w-3.5 text-slate-400" />
                      <h3 className="text-[13px] font-semibold tracking-[-0.01em]">{group.label}</h3>
                      <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10.5px] font-semibold text-slate-600 tabular-nums">
                        {group.items.length}
                      </span>
                    </button>
                    {groupOpen && (
                      <div className="space-y-1 pl-7">
                        {group.items.map((ws) => {
                          const isEditing = editingWsId === ws.wsId;
                          return (
                            <div
                              key={ws.wsId}
                              data-testid={`workspace-row-${org.orgId}-${ws.wsId}`}
                              className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 transition-colors duration-100 hover:border-slate-200 hover:bg-slate-50"
                            >
                              <div className="min-w-0 flex-1">
                                {isEditing ? (
                                  <Input
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') commitEditing(ws);
                                      else if (e.key === 'Escape') cancelEditing();
                                    }}
                                    className="h-7 text-xs"
                                    autoFocus
                                    data-testid={`workspace-rename-input-${org.orgId}-${ws.wsId}`}
                                  />
                                ) : (
                                  <>
                                    <p className="truncate text-[12.5px] font-[550] leading-tight text-slate-800">{ws.name || ws.wsId}</p>
                                    <p className="truncate font-mono text-[11px] leading-tight text-slate-500">{ws.wsId}</p>
                                  </>
                                )}
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                {isEditing ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => commitEditing(ws)}
                                      disabled={renameWsMutation.isPending}
                                      data-testid={`workspace-rename-save-${org.orgId}-${ws.wsId}`}
                                      aria-label="Save"
                                    >
                                      <Check className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={cancelEditing}
                                      disabled={renameWsMutation.isPending}
                                      data-testid={`workspace-rename-cancel-${org.orgId}-${ws.wsId}`}
                                      aria-label="Cancel"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      data-testid={`workspace-rename-${org.orgId}-${ws.wsId}`}
                                      onClick={() => startEditing(ws)}
                                      aria-label={`Rename ${ws.name || ws.wsId}`}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      data-testid={`workspace-clone-${org.orgId}-${ws.wsId}`}
                                      onClick={() => setWsToClone(ws)}
                                      aria-label={`Clone ${ws.name || ws.wsId}`}
                                    >
                                      <Copy className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      data-testid={`workspace-delete-${org.orgId}-${ws.wsId}`}
                                      onClick={() => setWsToDelete(ws)}
                                      aria-label={`Delete ${ws.name || ws.wsId}`}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!wsToDelete}
        onOpenChange={(open) => !open && setWsToDelete(null)}
        title="Delete workspace?"
        description={
          wsToDelete
            ? `This will permanently delete "${wsToDelete.name || wsToDelete.wsId}" (${wsToDelete.wsId}).`
            : ''
        }
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteWsMutation.isPending}
        onConfirm={() => {
          if (wsToDelete) deleteWsMutation.mutate(wsToDelete.wsId);
        }}
        testId="workspace-delete-confirm"
      />

      <CloneWorkspaceDialog
        source={wsToClone}
        sourceOrgId={org.orgId}
        existingWsIds={workspaces.map((w) => w.wsId)}
        onClose={() => setWsToClone(null)}
        onCloned={(targetOrgId) => {
          queryClient.invalidateQueries({ queryKey: ['org-workspaces', targetOrgId] });
          setWsToClone(null);
        }}
      />
    </section>
  );
}

interface CloneWorkspaceDialogProps {
  source: WorkspaceInfo | null;
  sourceOrgId: string;
  existingWsIds: string[];
  onClose: () => void;
  onCloned: (targetOrgId: string) => void;
}

function CloneWorkspaceDialog({ source, sourceOrgId, existingWsIds, onClose, onCloned }: CloneWorkspaceDialogProps) {
  const [targetWsId, setTargetWsId] = useState('');
  const [targetName, setTargetName] = useState('');

  const trimmedWsId = targetWsId.trim();
  const trimmedName = targetName.trim();
  const idConflict = trimmedWsId !== '' && existingWsIds.includes(trimmedWsId);
  const canSubmit = trimmedWsId !== '' && trimmedName !== '' && !idConflict;

  const cloneMutation = useMutation({
    mutationFn: () => {
      if (!source) throw new Error('No source workspace');
      return transferService.clone(sourceOrgId, source.wsId, {
        targetOrgId: sourceOrgId,
        targetWsId: trimmedWsId,
        targetName: trimmedName,
        format: 'yaml',
        clean: false,
      });
    },
    onSuccess: (response) => {
      toast.success('Workspace cloned', { description: response.message });
      onCloned(response.targetWorkspace.orgId);
      setTargetWsId('');
      setTargetName('');
    },
    onError: (error) => {
      toast.error('Clone failed', {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });

  const open = !!source;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      onClose();
      setTargetWsId('');
      setTargetName('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Clone Workspace</DialogTitle>
          <DialogDescription>
            {source
              ? `Cloning "${source.name || source.wsId}" within ${sourceOrgId}.`
              : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="clone-target-ws-id">
              Target Workspace ID <span className="text-destructive">*</span>
            </Label>
            <Input
              id="clone-target-ws-id"
              placeholder={source ? `${source.wsId}-copy` : ''}
              value={targetWsId}
              onChange={(e) => setTargetWsId(e.target.value)}
              aria-invalid={idConflict}
              required
            />
            {idConflict && (
              <p className="text-xs text-destructive" data-testid="workspace-clone-id-conflict">
                A workspace with this ID already exists in {sourceOrgId}.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="clone-target-name">
              Target Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="clone-target-name"
              placeholder="Workspace display name"
              value={targetName}
              onChange={(e) => setTargetName(e.target.value)}
              required
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={cloneMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => cloneMutation.mutate()}
            disabled={!canSubmit || cloneMutation.isPending}
            data-testid="workspace-clone-confirm"
          >
            {cloneMutation.isPending ? 'Cloning...' : 'Clone'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
